import { createFileRoute } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { trpc } from '@/lib/trpc';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { Layout } from './-components/layout';
import { PageSeo } from './-components/page-seo';
import { slugs } from './partner-ngos.meta';
import { Hero } from './-components/partner-ngos/hero';
import { NgoDirectory } from './-components/partner-ngos/ngo-directory';
import { Testimonials } from './-components/partner-ngos/testimonials';
import { HowToCollaborate } from './-components/partner-ngos/how-to-collaborate';
import { NgoAreaBand } from './-components/partner-ngos/ngo-area-band';
import { Faq, useFaqItems } from './-components/partner-ngos/faq';

export const Route = createFileRoute('/new/partner-ngos')({
	loader: ({ context }) => {
		// Warm the two feeds the page opens with: the partner roster behind the directory and the
		// global stats behind the hero's counter. `prefetchQuery`, not `ensureQueryData` — see the
		// homepage loader for why a floating rejected promise here takes the server process down.
		context.queryClient.prefetchQuery(trpc.portal.getPartnerNgos.queryOptions());
		context.queryClient.prefetchQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	},
	component: Component,
});

function Component() {
	const { t } = useLingui();
	const faqItems = useFaqItems();

	// Built from the same array the accordion renders, so the markup can never answer a question the
	// page does not show. Requested on the ticket to make the page eligible for an FAQ rich result.
	const faqLd = {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		'mainEntity': faqItems.map((item) => ({
			'@type': 'Question',
			'name': item.question,
			// The bulleted answer has to be flattened into the one string schema.org allows, or the
			// rich result would quote only the sentence that introduces the list.
			'acceptedAnswer': {
				'@type': 'Answer',
				'text': item.bullets ? `${item.answer} ${item.bullets.join(' ')}` : item.answer,
			},
		})),
	};

	return (
		<Layout>
			<Header />
			<PageSeo
				slugs={slugs}
				title={t`Partner NGOs - Worldcoo`}
				description={t`The social entities we work with to turn microdonations into funded projects. Discover the NGOs that are part of Worldcoo and how to start collaborating.`}
				jsonLd={[faqLd]}
			/>
			<Hero />
			{/* Owns its own Suspense and error boundary: its heading is static copy and renders either
			    way, so only the filters and rows wait on — or degrade with — the roster. */}
			<NgoDirectory />
			<Testimonials />
			<HowToCollaborate />
			<NgoAreaBand />
			<Faq />
			<Footer cta="form" />
		</Layout>
	);
}
